import { Minus, Plus } from "@cronika/assets/icons";
import { useTranslation } from "@cronika/locales";
import { Button, cn } from "@cronika/ui";
import { FC, useEffect, useRef, useState } from "react";
import { EMoneySelectorType } from "../../types";
import { useMediaQuery } from "usehooks-ts";

const CalculatorIcon: FC<{ className?: string }> = ({ className }) => (
  <svg className={className} width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3.5" y="2.5" width="13" height="15" rx="2" stroke="currentColor" strokeWidth="1.5" />
    <path d="M6 6H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <rect x="6" y="9.5" width="2" height="2" rx="0.5" fill="currentColor" />
    <rect x="9" y="9.5" width="2" height="2" rx="0.5" fill="currentColor" />
    <rect x="12" y="9.5" width="2" height="2" rx="0.5" fill="currentColor" />
    <rect x="6" y="13" width="2" height="2" rx="0.5" fill="currentColor" />
    <rect x="9" y="13" width="2" height="2" rx="0.5" fill="currentColor" />
    <rect x="12" y="13" width="2" height="2" rx="0.5" fill="currentColor" />
  </svg>
);

type TMoneySelectorProps = {
  type?: EMoneySelectorType;
  onAmountChange?: (amount: string) => void;
  defaultAmount?: string;
  value?: string;
  maxBalance?: number;
  minAmount?: number;
  maxAmount?: number;
};

const MIN_AMOUNT = 0;
const MAX_AMOUNT = 5000;
const MIN_MULTIPLIER = 2;
const MAX_MULTIPLIER = 10;

const stepRanges = [
  { max: 20, step: 1 },
  { max: 100, step: 2 },
  { max: 300, step: 5 },
  { max: 700, step: 10 },
  { max: 1500, step: 25 },
  { max: 3000, step: 50 },
  { max: 5000, step: 100 },
];

function getStep(value: number): number {
  for (const range of stepRanges) {
    if (value < range.max) return range.step;
  }
  return stepRanges[stepRanges.length - 1]?.step || 10;
}

export const MoneySelector: FC<TMoneySelectorProps> = ({
  type = EMoneySelectorType.DROPDOWN,
  onAmountChange,
  defaultAmount = "20",
  value,
  maxBalance,
  minAmount,
  maxAmount,
}) => {
  const { t } = useTranslation("trading");
  const [amount, setAmount] = useState(value || defaultAmount);
  const [inputValue, setInputValue] = useState(value || defaultAmount);
  const [multiplier, setMultiplier] = useState("2");
  const [isEditing, setIsEditing] = useState(false);
  const [isCalcOpen, setIsCalcOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const isHorizontal = useMediaQuery("(max-height: 390px)");

  useEffect(() => {
    if (value !== undefined && value !== amount) {
      setAmount(value);
      if (!isEditing) setInputValue(value);
    }
  }, [value, amount, isEditing]);

  const effectiveMin = minAmount !== undefined ? Math.max(MIN_AMOUNT, minAmount) : MIN_AMOUNT;
  const effectiveMax = (() => {
    const base = maxAmount !== undefined ? Math.min(MAX_AMOUNT, maxAmount) : MAX_AMOUNT;
    return maxBalance ? Math.min(base, maxBalance) : base;
  })();

  const clamp = (val: number): number => {
    return Math.max(effectiveMin, Math.min(val, effectiveMax));
  };

  const commitAmount = (newAmount: number) => {
    const clamped = clamp(newAmount);
    const str = parseFloat(clamped.toFixed(2)).toString();
    setAmount(str);
    setInputValue(str);
    onAmountChange?.(str);
  };

  useEffect(() => {
    const handleOnboardingValue = (event: Event) => {
      const detail = (event as CustomEvent<{ amount?: number }>).detail;
      if (detail?.amount === undefined) return;
      const next = String(detail.amount);
      setAmount(next);
      setInputValue(next);
      onAmountChange?.(next);
    };
    window.addEventListener("cronika:onboarding-set-values", handleOnboardingValue);
    return () => window.removeEventListener("cronika:onboarding-set-values", handleOnboardingValue);
  });

  const currentNumericAmount = Number(amount) || 0;
  const canGoNext = currentNumericAmount < effectiveMax;
  const canGoPrevious = currentNumericAmount > effectiveMin;

  const goToPrevious = () => {
    if (!canGoPrevious) return;
    const step = getStep(currentNumericAmount - 1);
    const snapped = Math.floor(currentNumericAmount / step) * step;
    const newAmount = snapped < currentNumericAmount ? snapped : currentNumericAmount - step;
    commitAmount(Math.max(newAmount, MIN_AMOUNT));
  };

  const goToNext = () => {
    if (!canGoNext) return;
    const step = getStep(currentNumericAmount);
    const snapped = Math.ceil(currentNumericAmount / step) * step;
    const newAmount = snapped > currentNumericAmount ? snapped : currentNumericAmount + step;
    commitAmount(Math.min(newAmount, effectiveMax));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/[^0-9.]/g, "");
    const parts = val.split(".");
    if (parts.length > 2) return;
    if (parts[1] && parts[1].length > 2) return;
    setInputValue(val);
  };

  const handleInputBlur = () => {
    setIsEditing(false);
    const num = Number(inputValue) || 0;
    commitAmount(num);
  };

  const handleInputFocus = () => {
    setIsEditing(true);
    if (inputRef.current) {
      inputRef.current.select();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      inputRef.current?.blur();
    }
  };

  const clampMultiplier = (val: number): number => {
    return Math.max(MIN_MULTIPLIER, Math.min(val, MAX_MULTIPLIER));
  };

  const handleMultiplierChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/[^0-9]/g, "");
    setMultiplier(val);
  };

  const handleMultiplierBlur = () => {
    const num = Number(multiplier) || MIN_MULTIPLIER;
    setMultiplier(clampMultiplier(num).toString());
  };

  const multiplyAmount = () => {
    const mul = clampMultiplier(Number(multiplier) || MIN_MULTIPLIER);
    const newAmount = Math.round(currentNumericAmount * mul);
    commitAmount(newAmount);
  };

  const divideAmount = () => {
    const mul = clampMultiplier(Number(multiplier) || MIN_MULTIPLIER);
    const newAmount = Math.round(currentNumericAmount / mul);
    commitAmount(Math.max(newAmount, MIN_AMOUNT));
  };

  const triggerContent = (
    <div
      className={cn(
        "flex items-center w-full md:px-4 py-1",
        isHorizontal ? "h-10 justify-center px-4" : "h-12 justify-between px-2",
      )}
    >
      <div
        className={cn(
          "flex flex-col gap-1",
          isHorizontal ? "justify-between w-full flex-row items-center" : "items-start",
        )}
      >
        {!isHorizontal && <p className="text-[12px] text-default-500 leading-3">{t("amount-label")}, $</p>}
        {isHorizontal && (
          <Button
            variant="ghost-v2"
            size="lg"
            isIconOnly
            className="w-7 h-4"
            disabled={!canGoPrevious}
            onClick={goToPrevious}
          >
            <Minus />
          </Button>
        )}
        <div className="flex justify-center items-center">
          {!isHorizontal && <span className="text-[16px]">$</span>}
          <input
            ref={inputRef}
            value={isEditing ? inputValue : amount}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
            inputMode="decimal"
            style={isHorizontal ? { width: `${Math.max(1, (isEditing ? inputValue : amount).length)}ch` } : undefined}
            className={cn("text-[16px] bg-transparent outline-none", isHorizontal ? "text-center" : "w-20")}
          />
          {isHorizontal && <span className="text-[16px] ml-1">$</span>}
        </div>
        {isHorizontal && (
          <Button variant="ghost-v2" size="lg" isIconOnly className="w-7 h-4" disabled={!canGoNext} onClick={goToNext}>
            <Plus />
          </Button>
        )}
      </div>
      {!isHorizontal && (
        <div className="flex items-center gap-2">
          <input
            value={multiplier}
            onChange={handleMultiplierChange}
            onBlur={handleMultiplierBlur}
            inputMode="numeric"
            className="w-[26px] h-8.5 text-center text-[16px] rounded-xl bg-zinc-900 border-1 border-glass/4"
          />
          <div className="flex flex-row gap-1">
            <Button
              variant="ghost-v2"
              size="sm"
              radius="lg"
              isIconOnly
              className="h-5.5 w-4.25 md:w-7 text-[17px] leading-4 rounded-[8px] pt-[5px]"
              onClick={multiplyAmount}
            >
              *
            </Button>
            <Button
              variant="ghost-v2"
              size="sm"
              radius="lg"
              isIconOnly
              className="h-5.5 w-4.25 md:w-7 text-[17px] leading-4"
              onClick={divideAmount}
            >
              ÷
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  if (!isHorizontal && type === EMoneySelectorType.DROPDOWN) {
    return (
      <div className="flex w-full flex-col gap-2">
        <div
          data-tour-target="amount-block"
          className="flex w-full items-center justify-between rounded-lg bg-glass/4 px-2.5 py-1.5"
        >
          <div className="flex flex-col">
            <p className="text-[12px] text-default-500">{t("amount-label")}, $</p>
            <div className="flex items-center">
              <span className="text-[16px]">$</span>
              <input
                ref={inputRef}
                value={isEditing ? inputValue : amount}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                onKeyDown={handleKeyDown}
                inputMode="decimal"
                className="text-[16px] bg-transparent outline-none w-20"
              />
            </div>
          </div>
          {isCalcOpen && (
            <input
              value={multiplier}
              onChange={handleMultiplierChange}
              onBlur={handleMultiplierBlur}
              inputMode="numeric"
              className="h-9 w-9 shrink-0 rounded-xl border border-glass/10 bg-glass/8 text-center text-[16px] outline-none"
            />
          )}
        </div>

        {isCalcOpen && (
          <div className="grid grid-cols-[1fr_30px_1fr] gap-1">
            <Button
              variant="ghost-v2"
              isIconOnly
              radius="lg"
              className="w-full h-5.5 text-[17px] leading-4"
              onClick={divideAmount}
            >
              ÷
            </Button>
            <Button
              variant="ghost-v2"
              isIconOnly
              radius="lg"
              className="w-full h-5.5"
              onClick={() => setIsCalcOpen(false)}
            >
              <CalculatorIcon className="h-4 w-4 text-warning" />
            </Button>
            <Button
              variant="ghost-v2"
              isIconOnly
              radius="lg"
              className="w-full h-5.5 text-[17px] leading-4 pt-[5px]"
              onClick={multiplyAmount}
            >
              *
            </Button>
          </div>
        )}

        <div className={cn("grid gap-1", isCalcOpen ? "grid-cols-2" : "grid-cols-[1fr_30px_1fr]")}>
          <Button
            data-tour-target="amount-minus"
            variant="ghost-v2"
            isIconOnly
            radius="lg"
            className="w-full h-5.5"
            disabled={!canGoPrevious}
            onClick={goToPrevious}
          >
            <Minus />
          </Button>
          {!isCalcOpen && (
            <Button
              variant="ghost-v2"
              isIconOnly
              radius="lg"
              className="w-full h-5.5"
              onClick={() => setIsCalcOpen(true)}
            >
              <CalculatorIcon className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="ghost-v2"
            isIconOnly
            radius="lg"
            className="w-full h-5.5"
            disabled={!canGoNext}
            onClick={goToNext}
          >
            <Plus />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-2">
      <div
        data-tour-target="amount-block"
        className={`bg-glass/4 w-full border-1 border-glass/10 rounded-lg [@media(max-height:390px)]:min-h-10 ${
          type === EMoneySelectorType.DRAWER ? "min-h-10" : "min-h-14"
        } flex items-center`}
      >
        {triggerContent}
      </div>
      {!isHorizontal && (
        <div className="hidden md:flex gap-2 w-full">
          <Button
            data-tour-target="amount-minus"
            variant="ghost-v2"
            isIconOnly
            className="w-full h-8.5"
            disabled={!canGoPrevious}
            onClick={goToPrevious}
          >
            <Minus />
          </Button>
          <Button variant="ghost-v2" isIconOnly className="w-full h-8.5" disabled={!canGoNext} onClick={goToNext}>
            <Plus />
          </Button>
        </div>
      )}
    </div>
  );
};
